import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../API/Auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PolicyDocumentModal from "../Components/PolicyDocumentModal";
import { tokens } from "../theme/tokens";
import {
  COUNTRY_NAMES,
  DEFAULT_COUNTRY,
  statesForCountry,
} from "./signupGeo";

const companySizes = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1000+ employees",
];

const companyRoles = [
  "CEO/Founder",
  "Manager",
  "HR",
  "Finance",
  "Operations",
  "Sales",
  "Marketing",
  "Other",
];

const bookingRegions = [
  { label: "Domestic", value: "Domestic" },
  { label: "International", value: "International" },
  { label: "Both", value: "Both" },
];

const companySizeToEmployeeSize = {
  "1-10 employees": 10,
  "11-50 employees": 50,
  "51-200 employees": 200,
  "201-500 employees": 500,
  "501-1000 employees": 1000,
  "1000+ employees": 1500,
};

const DECLARATIONS = [
  {
    key: "authority",
    title: "Authority Declaration",
    text: "I confirm that I am duly authorized to create and operate this account on behalf of the concerned business / entity.",
  },
  {
    key: "informationAccuracy",
    title: "Information Accuracy Declaration",
    text: "I confirm that all information and documents submitted by me are true, correct, valid and up-to-date. I understand that false, forged, misleading or incomplete information may result in rejection, suspension, termination of account, withholding of bookings / payments and legal action.",
  },
  {
    key: "b2bUse",
    title: "B2B Use Declaration",
    text: "I confirm that this account is being created for business / commercial use only and not for personal consumer use.",
  },
  {
    key: "dataProcessing",
    title: "Data Processing Consent",
    text: "I consent to collection, storage, verification, processing and sharing of submitted information and documents with relevant suppliers, aggregators, hotels, payment partners, service providers and governmental / regulatory authorities for verification, booking, operational, compliance, payment and legal purposes.",
  },
  {
    key: "communication",
    title: "Communication Consent",
    text: "I consent to receive operational, transactional, verification, support, compliance, payment, recovery and promotional communications from TRIP TRIP through phone calls, SMS, WhatsApp, email, notifications and other electronic means.",
  },
];

const INITIAL_DECLARATIONS = {
  authority: false,
  informationAccuracy: false,
  b2bUse: false,
  dataProcessing: false,
  communication: false,
  termsAcceptance: false,
};

const POLICY_DOCUMENT_URLS = {
  termsOfUse:
    "https://trip-trip-assets.s3.ap-south-1.amazonaws.com/Term%20of%20use.pdf",
  privacyPolicy:
    "https://trip-trip-assets.s3.ap-south-1.amazonaws.com/Privacy%20Policy.pdf",
  refundCancellation:
    "https://trip-trip-assets.s3.ap-south-1.amazonaws.com/Refund%20&%20Cancellation%20Policy.pdf",
  disclaimer:
    "https://trip-trip-assets.s3.ap-south-1.amazonaws.com/Disclaimer.pdf",
  userAgreement:
    "https://trip-trip-assets.s3.ap-south-1.amazonaws.com/User%20Agreement.pdf",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    minHeight: 52,
  },
  "& .MuiOutlinedInput-input": {
    py: 1.5,
    fontSize: "0.9375rem",
  },
  "& .MuiInputLabel-root": { fontSize: "0.9375rem" },
};

const linkSx = {
  color: tokens.brand,
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": { color: tokens.accent },
};

function isClientSignupSuccess(data) {
  if (data == null) return true;
  if (data.error === true || data.success === false || data.status === false) return false;
  const code = data.code != null ? Number(data.code) : null;
  if (code != null && code !== 200 && code !== 201) return false;
  return true;
}

const Signup = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    clientName: "",
    bookingRegion: "",
    contactPerson: "",
    roleInCompany: "",
    companyEmployeeSize: "",
    contactEmail: "",
    contactPhone: "",
    gstNumber: "",
    panNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: DEFAULT_COUNTRY,
    postalCode: "",
    referralCode: "",
    email: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [declarations, setDeclarations] = useState(INITIAL_DECLARATIONS);
  const [declarationErrors, setDeclarationErrors] = useState({});

  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);

  const allDeclarationsAccepted = Object.values(declarations).every(Boolean);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    return phone.length === 10 && /^\d+$/.test(phone);
  };

  const validateGstNumber = (gst) => {
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return !gst || regex.test(gst);
  };

  const validatePanNumber = (pan) => {
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return !pan || regex.test(pan);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      setFormData((prev) => ({ ...prev, country: value, state: "" }));
      setErrors((prev) => ({ ...prev, country: "", state: "" }));
      return;
    }
    if (name === "phoneNumber" || name === "contactPhone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else if (name === "gstNumber" || name === "panNumber") {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDeclarationChange = (key) => (e) => {
    setDeclarations((prev) => ({ ...prev, [key]: e.target.checked }));
    setDeclarationErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const openPolicyLink = (e, openModal) => {
    e.preventDefault();
    e.stopPropagation();
    openModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const newDeclarationErrors = {};

    if (!formData.contactPerson.trim()) newErrors.contactPerson = "Contact person is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Invalid email address";
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
    else if (!validatePhone(formData.phoneNumber)) newErrors.phoneNumber = "Invalid phone number";

    if (!formData.contactEmail.trim()) newErrors.contactEmail = "Contact email is required";
    else if (!validateEmail(formData.contactEmail)) newErrors.contactEmail = "Invalid contact email";
    if (!formData.contactPhone.trim()) newErrors.contactPhone = "Contact phone is required";
    else if (!validatePhone(formData.contactPhone)) newErrors.contactPhone = "Invalid contact phone";

    if (!formData.clientName.trim()) newErrors.clientName = "Client name is required";
    if (!formData.bookingRegion) newErrors.bookingRegion = "Booking region is required";
    if (!formData.companyEmployeeSize) newErrors.companyEmployeeSize = "Company size is required";
    if (!formData.roleInCompany) newErrors.roleInCompany = "Role in company is required";
    if (!formData.gstNumber.trim()) newErrors.gstNumber = "GST number is required";
    else if (!validateGstNumber(formData.gstNumber)) newErrors.gstNumber = "Invalid GST number";
    if (!formData.panNumber.trim()) newErrors.panNumber = "PAN is required";
    else if (!validatePanNumber(formData.panNumber)) newErrors.panNumber = "Invalid PAN";
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = "Address line 1 is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.postalCode.trim()) newErrors.postalCode = "Postal code is required";

    Object.entries(declarations).forEach(([key, accepted]) => {
      if (!accepted) {
        newDeclarationErrors[key] = "Required";
      }
    });

    if (Object.keys(newErrors).length > 0 || Object.keys(newDeclarationErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      setDeclarationErrors(newDeclarationErrors);
      if (Object.keys(newDeclarationErrors).length > 0) {
        toast.error("Please accept all declarations before signing up.");
      }
      return;
    }

    setIsLoading(true);

    try {
      const employeeSize = formData.companyEmployeeSize
        ? companySizeToEmployeeSize[formData.companyEmployeeSize] ?? 0
        : 0;

      const payload = {
        clientName: formData.clientName.trim(),
        bookingRegion: formData.bookingRegion,
        contactPerson: formData.contactPerson.trim(),
        roleInCompany: formData.roleInCompany,
        employeeSize,
        contactEmail: formData.contactEmail.trim(),
        contactPhone: formData.contactPhone,
        gstNumber: formData.gstNumber.trim(),
        panNumber: formData.panNumber.trim(),
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        city: formData.city.trim(),
        state: formData.state,
        country: formData.country,
        postalCode: formData.postalCode.trim(),
        referralCode: formData.referralCode.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
      };

      const response = await authAPI.register(payload);

      if (!isClientSignupSuccess(response)) {
        toast.error(response?.message || "Registration failed. Please try again.");
        return;
      }

      setTimeout(
        () =>
          navigate("/signup-success", {
            state: {
              clientName: formData.clientName.trim(),
              email: formData.email.trim(),
            },
          }),
        800
      );
    } catch (error) {
      console.error("Registration error:", error);
      const msg =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        error.message ||
        "Registration failed. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const stateOptions = statesForCountry(formData.country);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: { xs: 2, md: 3 },
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer />
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            width: "100%",
            borderRadius: { xs: "16px", sm: "20px" },
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
            <img src="/images/Wordmark.svg" alt="TripTrip" style={{ height: 24, width: "auto" }} />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 600,
              textAlign: "center",
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
              background: `linear-gradient(45deg, ${tokens.brand} 30%, ${tokens.accent} 90%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            B2B Sign Up
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Client name"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  error={!!errors.clientName}
                  helperText={errors.clientName}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Booking region"
                  name="bookingRegion"
                  value={formData.bookingRegion}
                  onChange={handleChange}
                  error={!!errors.bookingRegion}
                  helperText={errors.bookingRegion}
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select booking region</em>
                  </MenuItem>
                  {bookingRegions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  placeholder="10-digit mobile"
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  error={!!errors.contactPerson}
                  helperText={errors.contactPerson}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Role in company"
                  name="roleInCompany"
                  value={formData.roleInCompany}
                  onChange={handleChange}
                  error={!!errors.roleInCompany}
                  helperText={errors.roleInCompany}
                  sx={fieldSx}
                >
                  {companyRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Employee size"
                  name="companyEmployeeSize"
                  value={formData.companyEmployeeSize}
                  onChange={handleChange}
                  error={!!errors.companyEmployeeSize}
                  helperText={errors.companyEmployeeSize}
                  sx={fieldSx}
                >
                  {companySizes.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact email"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact phone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  error={!!errors.contactPhone}
                  helperText={errors.contactPhone}
                  placeholder="10-digit mobile"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST number"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  error={!!errors.gstNumber}
                  helperText={errors.gstNumber || "Format: 22AAAAA0000A1Z5"}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PAN"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  error={!!errors.panNumber}
                  helperText={errors.panNumber || "Format: ABCDE1234F"}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referral code (optional)"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  error={!!errors.referralCode}
                  helperText={errors.referralCode}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address line 1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  error={!!errors.addressLine1}
                  helperText={errors.addressLine1}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address line 2 (optional)"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  error={!!errors.addressLine2}
                  helperText={errors.addressLine2}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  error={!!errors.country}
                  helperText={errors.country}
                  sx={fieldSx}
                >
                  {COUNTRY_NAMES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                {stateOptions.length > 0 ? (
                  <TextField
                    fullWidth
                    select
                    label="State / Province"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    error={!!errors.state}
                    helperText={errors.state}
                    sx={fieldSx}
                  >
                    <MenuItem value="">
                      <em>Select state</em>
                    </MenuItem>
                    {stateOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    label="State / Province"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    error={!!errors.state}
                    helperText={errors.state}
                    sx={fieldSx}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Postal code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: tokens.text, fontSize: "0.8125rem" }}
            >
              Declarations &amp; Consents
            </Typography>

            <Grid container spacing={0.75}>
              {DECLARATIONS.map(({ key, title, text }) => (
                <Grid item xs={12} key={key}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: "8px",
                      border: declarationErrors[key]
                        ? `1px solid ${tokens.error}`
                        : "1px solid transparent",
                      bgcolor: declarationErrors[key] ? tokens.errorSoft : "transparent",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        alignItems: "flex-start",
                        m: 0,
                        "& .MuiCheckbox-root": { pt: 0.25, pb: 0.25 },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={declarations[key]}
                          onChange={handleDeclarationChange(key)}
                          sx={{ color: tokens.brand, "&.Mui-checked": { color: tokens.brand } }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: tokens.text, lineHeight: 1.5, fontSize: "0.8125rem" }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {title}:
                          </Box>{" "}
                          {text}
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: "8px",
                    border: declarationErrors.termsAcceptance
                      ? `1px solid ${tokens.error}`
                      : "1px solid transparent",
                    bgcolor: declarationErrors.termsAcceptance ? tokens.errorSoft : "transparent",
                  }}
                >
                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      m: 0,
                      "& .MuiCheckbox-root": { pt: 0.25, pb: 0.25 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={declarations.termsAcceptance}
                        onChange={handleDeclarationChange("termsAcceptance")}
                        sx={{ color: tokens.brand, "&.Mui-checked": { color: tokens.brand } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: tokens.text, lineHeight: 1.5, fontSize: "0.8125rem" }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Terms Acceptance:
                        </Box>{" "}
                        I have read, understood and agree to be legally bound by TRIP TRIP&apos;s{" "}
                        <Box
                          component="span"
                          sx={linkSx}
                          onClick={(e) => openPolicyLink(e, setTermsOpen)}
                        >
                          Terms of Use
                        </Box>
                        ,{" "}
                        <Box
                          component="span"
                          sx={linkSx}
                          onClick={(e) => openPolicyLink(e, setPrivacyOpen)}
                        >
                          Privacy Policy
                        </Box>
                        ,{" "}
                        <Box
                          component="span"
                          sx={linkSx}
                          onClick={(e) => openPolicyLink(e, setRefundOpen)}
                        >
                          Refund &amp; Cancellation Policy
                        </Box>
                        ,{" "}
                        <Box
                          component="span"
                          sx={linkSx}
                          onClick={(e) => openPolicyLink(e, setDisclaimerOpen)}
                        >
                          Platform Disclaimer
                        </Box>{" "}
                        and{" "}
                        <Box
                          component="span"
                          sx={linkSx}
                          onClick={(e) => openPolicyLink(e, setB2bOpen)}
                        >
                          B2B User Agreement
                        </Box>
                        .
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            {Object.keys(declarationErrors).length > 0 && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                All declarations must be accepted to complete signup.
              </Typography>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isLoading || !allDeclarationsAccepted}
                sx={{
                  borderRadius: "24px",
                  py: 1.1,
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${tokens.brand} 30%, ${tokens.accent} 90%)`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${tokens.accent} 30%, ${tokens.brandDeep} 90%)`,
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  },
                  "&.Mui-disabled": {
                    background: tokens.borderStrong,
                    color: tokens.textMuted,
                  },
                }}
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : "Sign Up"}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>

      <PolicyDocumentModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        title="Terms of Use"
        url={POLICY_DOCUMENT_URLS.termsOfUse}
      />
      <PolicyDocumentModal
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        title="Privacy Policy"
        url={POLICY_DOCUMENT_URLS.privacyPolicy}
      />
      <PolicyDocumentModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Refund & Cancellation Policy"
        url={POLICY_DOCUMENT_URLS.refundCancellation}
      />
      <PolicyDocumentModal
        open={disclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
        title="Disclaimer"
        url={POLICY_DOCUMENT_URLS.disclaimer}
      />
      <PolicyDocumentModal
        open={b2bOpen}
        onClose={() => setB2bOpen(false)}
        title="User Agreement"
        url={POLICY_DOCUMENT_URLS.userAgreement}
      />
    </Box>
  );
};

export default Signup;
